import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

/* ── Reconciliation badge ───────────────────────────────────────────────────── */
function ReconBadge({ recon }) {
  if (!recon) return null;
  const ok = recon.status === 'reconciled';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10.5, fontWeight: 500, padding: '2px 7px', borderRadius: 99,
      border: `1px solid ${ok ? 'rgba(30,138,74,0.25)' : 'rgba(196,122,26,0.3)'}`,
      background: ok ? 'rgba(30,138,74,0.08)' : 'rgba(196,122,26,0.1)',
      color: ok ? 'var(--success, #1e8a4a)' : 'var(--warning, #c47a1a)',
      whiteSpace: 'nowrap',
    }}>
      {ok
        ? <ShieldCheck style={{ width: 11, height: 11 }} />
        : <AlertCircle  style={{ width: 11, height: 11 }} />}
      {ok ? 'Reconciled' : 'Partial'} · {recon.pct.toFixed(1)}%
    </span>
  );
}

/* ── KPI card ───────────────────────────────────────────────────────────────── */
export function KpiCard({ title, metric, delta, trend, description, icon: Icon, href, recon, color }) {
  const positive = trend === 'up';
  const iconBg   = color ? `${color}18` : 'var(--muted)';   // 10% opacity tint
  const iconFg   = color || 'var(--primary)';
  return (
    <Link
      to={href}
      className="group surface-card relative overflow-hidden p-5 flex flex-col justify-between transition-transform hover:-translate-y-0.5 hover:shadow-lg"
      aria-label={`${title}: ${metric}. Open dashboard`}
      style={{ textDecoration: 'none', color: 'inherit', minHeight: 168 }}
    >
      <div className="relative flex items-start justify-between">
        <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          {/* Title + recon badge inline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium" style={{ margin: 0 }}>
              {title}
            </p>
            <ReconBadge recon={recon} />
          </div>

          <p className="mt-3 font-display text-3xl font-semibold tracking-tight"
             style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            {metric}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>

        <div
          className="h-10 w-10 grid place-items-center rounded-lg flex-shrink-0 transition-all"
          style={{ background: iconBg, color: iconFg, border: `1px solid ${color ? `${color}30` : 'var(--border)'}` }}
          onMouseEnter={e => { e.currentTarget.style.background = iconFg; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = iconBg; e.currentTarget.style.color = iconFg; }}
        >
          {Icon && <Icon style={{ width: 16, height: 16 }} />}
        </div>
      </div>

      <div className="relative mt-4 flex items-center justify-between">
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          borderRadius: 99, border: '1px solid', padding: '2px 8px',
          fontSize: 11, fontWeight: 500,
          borderColor: positive ? 'rgba(30,138,74,0.3)' : 'rgba(217,79,58,0.3)',
          background:   positive ? 'rgba(30,138,74,0.1)' : 'rgba(217,79,58,0.1)',
          color:        positive ? 'var(--success)' : 'var(--destructive)',
        }}>
          {positive
            ? <ArrowUpRight  style={{ width: 12, height: 12 }} />
            : <ArrowDownRight style={{ width: 12, height: 12 }} />}
          {delta}
        </span>
        <span className="text-[11px] text-muted-foreground group-hover:text-primary transition-colors">
          Open dashboard →
        </span>
      </div>
    </Link>
  );
}
